// Role utility functions to handle role name normalization
// Backend uses proper case: 'Admin', 'Diretor Comercial', etc.
// Frontend checks for lowercase: 'admin', 'diretor_comercial', etc.

export const normalizeRoleForFrontend = (role: string): string => {
  // Convert backend role names to frontend format
  const roleMap: { [key: string]: string } = {
    'Admin': 'admin',
    'Diretor Comercial': 'diretor_comercial',
    'Team Leader': 'team_leader',
    'team_leader': 'team_leader', // Handle actual database value
    'Gestor de Equipa': 'gestor_equipa',
    'Gestor de Equipa Júnior': 'gestor_equipa_junior',
    'Gestor Comercial': 'gestor_comercial'
  };
  
  return roleMap[role] || role.toLowerCase();
};

export const normalizeRoleForBackend = (role: string): string => {
  // Convert frontend role names to backend format
  const roleMap: { [key: string]: string } = {
    'admin': 'Admin',
    'diretor_comercial': 'Diretor Comercial',
    'team_leader': 'team_leader', // Map to actual database value
    'gestor_equipa': 'Gestor de Equipa',
    'gestor_equipa_junior': 'Gestor de Equipa Júnior',
    'gestor_comercial': 'Gestor Comercial'
  };
  
  return roleMap[role] || role;
};

// Role checking functions
export const isAdmin = (role: string): boolean => {
  return normalizeRoleForFrontend(role) === 'admin';
};

/** Exact platform Admin role (not "tenant somehow became admin"). */
function isExactAdminRole(role: string): boolean {
  const r = role.toLowerCase().trim();
  return r === 'admin' || r === 'administrador';
}

/** Platform / tenant admin (export gates, tenant switcher, GRH user mgmt). */
export function isCrmAdminUser(authUser?: unknown): boolean {
  try {
    const check = (roleRaw: string, tenantRaw: string, businessAreaRaw = '') => {
      const role = String(roleRaw || '').toLowerCase();
      const tenant = String(tenantRaw || '').toLowerCase();
      const businessArea = String(businessAreaRaw || '').toLowerCase();
      // Exact Admin role is enough.
      if (isExactAdminRole(role)) return true;
      // Platform admin tenant only when role is also Admin — avoids GRH users
      // who were incorrectly rewritten to tenant=admin on login.
      if (tenant === 'admin' && isExactAdminRole(role)) return true;
      if (businessArea === 'admin' && isExactAdminRole(role)) return true;
      return false;
    };

    if (authUser) {
      const u = authUser as { role?: string; tenant?: string; business_area?: string };
      if (check(u.role || '', u.tenant || '', u.business_area || '')) return true;
    }
    const raw = localStorage.getItem('cognito_user');
    if (raw) {
      const u = JSON.parse(raw) as {
        role?: string;
        tenant?: string;
        business_area?: string;
        UserAttributes?: { Name: string; Value: string }[];
      };
      if (check(u.role || '', u.tenant || '', u.business_area || '')) return true;
      if (Array.isArray(u.UserAttributes)) {
        const attrs = u.UserAttributes.reduce<Record<string, string>>((acc, a) => {
          acc[a.Name] = a.Value;
          return acc;
        }, {});
        if (
          check(
            attrs['custom:role'] || '',
            attrs['custom:tenant'] || '',
            attrs['custom:business_area'] || '',
          )
        ) {
          return true;
        }
      }
    }
    const userRole = localStorage.getItem('userRole') || '';
    const userTenant = localStorage.getItem('user_tenant') || '';
    if (check(userRole, userTenant)) return true;
  } catch {
    // ignore parse errors
  }
  return false;
}

export const isAdminOrDC = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial';
};

export const isAdminOrDCOrTeamLeader = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial' || normalizedRole === 'team_leader';
};

export const canManageUsers = (role: string): boolean => {
  return isAdmin(role);
};

/**
 * Can the user reset other users' passwords and send invitation email (Admin, Coordenador per tenant, Gerente in Comercial).
 * Uses user.role and user.tenant (fallback: tenant from optional second arg or caller should pass merged user with tenant from localStorage).
 */
export const canResetUserPassword = (user: { role?: string; tenant?: string; business_area?: string } | null, tenantFromStorage?: string | null): boolean => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  const tenant = (user.tenant || tenantFromStorage || '').toLowerCase();
  const businessArea = (user.business_area || '').toLowerCase();
  if (role === 'admin' || tenant === 'admin' || businessArea === 'admin') return true;
  if (tenant === 'comercial' && (role.includes('gerente') || role.includes('gestor'))) return true;

  const isCccTenant =
    tenant === 'ccc' ||
    tenant === 'callcenter' ||
    businessArea.includes('callcenter') ||
    businessArea.includes('call center');
  const isCccManagerRole =
    role.includes('gerente') ||
    role.includes('coordenador') ||
    role.includes('coordinator') ||
    role.includes('call center manager') ||
    role.includes('commercial director');

  if (isCccTenant && isCccManagerRole) return true;
  // CCC UI sometimes stores role as "Gerente" while tenant is still loading — allow Call Center Manager role alone.
  if (role.includes('call center manager') || role.includes('commercial director')) return true;

  if (tenant === 'callcenter' && (role.includes('coordenador') || role.includes('manager'))) return true;
  if (tenant === 'backoffice' && (role.includes('coordenador') || role.includes('supervisor'))) return true;
  return false;
};

export const canManageTeams = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial' || normalizedRole === 'team_leader';
};

export const canAccessTeamsScreen = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'admin' || normalizedRole === 'diretor_comercial';
};

export const canSwitchBusinessArea = (role: string): boolean => {
  return isAdminOrDC(role);
};

export const getRoleLabel = (role: string): string => {
  const roleLabels: { [key: string]: string } = {
    'admin': 'Administrador',
    'diretor_comercial': 'Diretor Comercial',
    'team_leader': 'Team Leader',
    'gestor_equipa': 'Gestor de Equipa',
    'gestor_equipa_junior': 'Gestor de Equipa Júnior',
    'gestor_comercial': 'Gestor Comercial'
  };
  
  const normalizedRole = normalizeRoleForFrontend(role);
  return roleLabels[normalizedRole] || role;
};

export const getRoleColor = (role: string): string => {
  const roleColors: { [key: string]: string } = {
    'admin': 'red',
    'diretor_comercial': 'purple',
    'team_leader': 'blue',
    'gestor_equipa': 'green',
    'gestor_equipa_junior': 'orange',
    'gestor_comercial': 'cyan'
  };
  
  const normalizedRole = normalizeRoleForFrontend(role);
  return roleColors[normalizedRole] || 'default';
};

export const getBusinessAreaLabel = (area: string): string => {
  const areaLabels: { [key: string]: string } = {
    'telecommunications': 'Telecomunicações',
    'energy_solutions': 'Energia',
    'telecom': 'Telecomunicações', // Fallback for old values
    'energy': 'Energia' // Fallback for old values
  };
  
  return areaLabels[area] || area;
};

export const isTeamLeader = (role: string): boolean => {
  const normalizedRole = normalizeRoleForFrontend(role);
  return normalizedRole === 'team_leader';
};

export const canAccessSimuladores = (userRole: string, userBusinessArea: string, currentBusinessArea: string): boolean => {
  // Users in energy business area can always access
  if (userBusinessArea === 'energy_solutions') {
    return true;
  }
  
  // Admins and DCs can access if they've switched to energy side
  if (canSwitchBusinessArea(userRole) && currentBusinessArea === 'energy_solutions') {
    return true;
  }
  
  return false;
}; 